/**
 * VideoEmbedModal
 * Full-screen modal that plays a Thinkific-hosted lesson video inline via an
 * <iframe> (the Thinkific "videoproxy" URL is an embeddable Wistia player page,
 * not a raw media file — so it can't go in a <video> tag).
 *
 * Thinkific playUrls are short-lived signed URLs. Before showing the player we
 * check the stored URL's `expiry`; if it's stale we ask the backend to re-mint
 * a fresh one (GET /api/content/videos/:id/play-url) so playback never opens on
 * an expired link.
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { contentAPI } from '../services/api';
import { isSignedUrlStale } from '../utils/videoSource';
import styles from '../styles/VideoPlayerModal.module.css';

/**
 * Append autoplay so the embedded Wistia player starts on open. The signed
 * `verify` token covers `expiry` only, so extra player params are safe to add.
 */
function withAutoplay(url) {
  try {
    const u = new URL(url);
    u.searchParams.set('autoplay', 'true');
    return u.toString();
  } catch {
    return url;
  }
}

/**
 * Optional `resolveUrl` prop: async (playId) => { videoUrl } — overrides the
 * authenticated contentAPI play-url endpoint so pre-auth surfaces (the public
 * /workshop pages) can supply their own resolver. Same override pattern as
 * Quiz.jsx's onSubmitQuiz.
 */
function VideoEmbedModal({ playId, videoUrl, title, onClose, resolveUrl }) {
  const [resolvedUrl, setResolvedUrl] = useState(null);
  const [error, setError] = useState(false);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Resolve a fresh URL before rendering the iframe.
  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      // Stored URL still valid -> use it directly, no round-trip.
      if (videoUrl && !isSignedUrlStale(videoUrl)) {
        setResolvedUrl(videoUrl);
        return;
      }

      // Stale (or no expiry) -> ask the backend to re-mint a signed URL.
      try {
        const data = playId != null
          ? (resolveUrl ? await resolveUrl(playId) : await contentAPI.getVideoPlayUrl(playId))
          : null;
        if (cancelled) return;
        const fresh = data && data.videoUrl ? data.videoUrl : null;
        if (fresh) {
          setResolvedUrl(fresh);
        } else if (videoUrl) {
          // Refresh unavailable — fall back to the stored URL (may still work).
          setResolvedUrl(videoUrl);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('[VideoEmbedModal] Failed to refresh video URL:', err);
        if (cancelled) return;
        if (videoUrl) setResolvedUrl(videoUrl);
        else setError(true);
      }
    }

    resolve();
    return () => { cancelled = true; };
  }, [playId, videoUrl, resolveUrl]);

  // Lock body scroll + Escape-to-close while open.
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleOverlayClick = (e) => {
    e.stopPropagation();
    if (e.target === e.currentTarget) onCloseRef.current();
  };

  const handleClose = (e) => {
    e.stopPropagation();
    onCloseRef.current();
  };

  const modalContent = (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={handleClose} aria-label="Close video">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {title && <h2 className={styles.title}>{title}</h2>}

        {error ? (
          <div className={styles.loading}>Sorry, we couldn&apos;t load that video.</div>
        ) : resolvedUrl ? (
          <iframe
            className={styles.iframe}
            src={withAutoplay(resolvedUrl)}
            title={title || 'Video'}
            allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <div className={styles.loading}>Loading video…</div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

export default VideoEmbedModal;
